'use client'

import { usePathname, useSearchParams } from 'next/navigation'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useQuinceRouter } from '@src/modules/navigation'
import { trackingMetaData } from '@src/pages/[...slug]/components/collection/collection.constants'
import {
  getQueryParamsForSelectedFilters,
  getTrackerUrlForSelectedFilters,
} from '@src/pages/[...slug]/components/collection/collection.helpers'
import { SubCollection } from '@src/pages/[...slug]/components/collection/domains/Collection'
import {
  Filter,
  SelectedFilter,
  UpdateQueryParams,
} from '@src/pages/[...slug]/components/collection/domains/CollectionFilter'
import collectionService from '@src/pages/[...slug]/components/collection/services/collectionService/CollectionService'
import { IProductFeedResponseForCollection } from '@src/pages/[...slug]/components/collection/services/collectionService/CollectionService.types'
import useCollectionCache from '@src/pages/[...slug]/components/collection/useCollectionCache'
import { getUpdatedSelectedFilters, isUndefined } from '@src/pages/[...slug]/components/collection/utils'
import { SORT_BY } from '@src/quince-components/src/atoms/filterSection/constants/constants'
import { PRODUCT_SORT_VALUES } from '@src/quince-site-common/src/constants/product'
import { FEATURES, MODULES } from '@src/quince-site-common/src/constants/template-info'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import { useBatchedAnalyticsForProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsForProductCard'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import { useAllQueryState } from '@src/quince-site-common/src/hooks/useQueryState'
import useRecurringItemSubscriptionsExperiment from '@src/quince-site-common/src/hooks/useRecurringItemSubscriptionsExperiment'
import collectionCacheStateManager from '@src/quince-site-common/src/redux/globalStateManager/CollectionCacheStateManager'
import { captureException } from '@src/quince-site-common/src/utils/sentryUtil'
import { isBrowser } from '@src/quince-site-common/src/utils/windowUtil'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'

import { resetCollectionCacheOnFilterChange, handleFilterSectionScroll } from './utils'

const QUERY_KEYS: ('filter' | 'sortBy')[] = ['filter', 'sortBy']

export const useMiniPDPCollection = ({
  collectionId,
  color,
  productId,
}: Readonly<{
  collectionId: string
  color: string
  productId: string | number
}>) => {
  const pathname = usePathname()
  const searchParams = useSearchParams().toString()

  const {
    pageCache,
    hasPaginationTerminatedCache,
    currentCacheKey,
    scrollY,
    collectionPage: collectionPageCache,
  } = useCollectionCache({
    pathname: pathname,
    searchParams: searchParams,
  })

  const baseSortBy = PRODUCT_SORT_VALUES.FEATURED

  const [collectionPage, setCollectionPage] = useState<IProductFeedResponseForCollection | undefined>(
    collectionPageCache as IProductFeedResponseForCollection | undefined,
  )
  const [watchedQueryStates, setWatchedQueryStates] = useAllQueryState<'filter' | 'sortBy'>(
    QUERY_KEYS,
    undefined,
    false,
    true,
  )
  const { selectedFilters, sortBy } = useMemo(() => {
    const selectedFilters = watchedQueryStates.filter
      ? SelectedFilter.getSelectedFilterFromQueryParams(watchedQueryStates.filter)
      : new SelectedFilter({})
    const sortBy = watchedQueryStates.sortBy ?? SORT_BY.FEATURED.value

    return { selectedFilters, sortBy }
  }, [watchedQueryStates])

  const isI18nPreviewEnabled = useI18nPreviewContext()
  const isRecurringItemSubscriptionsExpEnabled = useRecurringItemSubscriptionsExperiment()

  const router = useQuinceRouter()
  const hasPaginationTerminated = useRef<boolean>(hasPaginationTerminatedCache ?? false)

  const productCardsSectionRef = useRef<HTMLDivElement>(null)

  const productViewTracker = `${pathname}_${TELEMETRY_EVENT_NAMES.BULK_PRODUCT_CARD_VIEW}`
  useBatchedAnalyticsForProductCard({
    productViewTracker,
    trackingMetaData,
  })

  const initialSubCollections = collectionPage?.subCollections ?? []

  const [filteredSubCollections, setFilteredSubCollections] = useState<SubCollection[]>(initialSubCollections)

  const [initialFilters, setInitialFilters] = useState<Filter[]>(collectionPage?.filters ?? [])
  const [nextFeedURL, setNextFeedURL] = useState<string>(
    collectionPageCache && 'next_url' in collectionPageCache ? collectionPageCache.next_url : '',
  )

  const [showSubCollectionsPageLoader, setShowSubCollectionsPageLoader] = useState<boolean>(
    !isUndefined(pageCache) ? false : true,
  )

  const totalProductsCurrentlyOnPage = filteredSubCollections[0]?.productCards?.length ?? 0

  const [totalProductsCount, setTotalProductsCount] = useState<number>(collectionPage?.total ?? 0)

  const filterSectionRef = useRef<HTMLDivElement>(null)
  const productSectionRef = useRef<HTMLDivElement>(null)

  // For infinite Feed.
  const intersectionOffset = isBrowser() ? window.innerHeight / 4 : 200
  const [intersectRef, isIntersecting] = useIntersect(null, `${intersectionOffset}px`, 0)
  const [showNextFeedLoading, setShowNextFeedLoading] = useState(false)
  const [page, setPage] = useState(pageCache ?? 0)

  const abortControllerRef = useRef<AbortController | null>(null)

  useEffect(() => {
    const controller = abortControllerRef.current
    return () => {
      if (controller) {
        controller.abort()
      }
    }
  }, [])

  useEffect(() => {
    if (!collectionPageCache) {
      const fetchCollectionPage = async () => {
        // Cancel any previous request
        if (abortControllerRef.current) {
          abortControllerRef.current.abort()
        }

        abortControllerRef.current = new AbortController()
        const signal = abortControllerRef.current.signal

        try {
          setShowSubCollectionsPageLoader(true)
          const queryParams = getQueryParamsForSelectedFilters(selectedFilters, sortBy)
          const collectionData = await collectionService.getCollectionById(
            {
              limit: 40,
              collectionId: collectionId,
              productIdForRecommendations: productId,
              colorForProductRecommendations: color,
              filter: queryParams.filter,
              sortBy: (queryParams.sortBy as PRODUCT_SORT_VALUES | undefined) ?? baseSortBy,
              abortSignal: signal,
            },
            isI18nPreviewEnabled,
            false,
            isRecurringItemSubscriptionsExpEnabled,
          )
          setCollectionPage(collectionData)

          const total = collectionData.total
          if (typeof total === 'number' && !isNaN(total)) {
            setTotalProductsCount(total)
          }

          setInitialFilters(collectionData.filters)
          setFilteredSubCollections(collectionData.subCollections)
          setPage(1)

          if (collectionData.next_url) setNextFeedURL(collectionData.next_url)
          if (!isNaN(collectionData.total)) setTotalProductsCount(collectionData.total)
        } catch (error) {
          captureException(error, {
            module: MODULES.MINI_PDP,
            feature: FEATURES.PAGINATION,
          })
        } finally {
          if (!signal.aborted) {
            setShowSubCollectionsPageLoader(false)
          }
        }
      }

      handleFilterSectionScroll(filterSectionRef)
      void fetchCollectionPage()
    } else {
      setCollectionPage(collectionPageCache as IProductFeedResponseForCollection)
      setFilteredSubCollections((collectionPageCache?.subCollections as SubCollection[]) ?? [])
      setNextFeedURL((collectionPageCache as IProductFeedResponseForCollection)?.next_url ?? '')
      setTotalProductsCount((collectionPageCache as IProductFeedResponseForCollection)?.total ?? 0)
      setInitialFilters(collectionPageCache?.filters ?? [])
      setPage(pageCache ?? 0)
    }
  }, [watchedQueryStates])

  useEffect(() => {
    if (isIntersecting && !hasPaginationTerminated.current && !showNextFeedLoading && !showSubCollectionsPageLoader) {
      if (totalProductsCount <= totalProductsCurrentlyOnPage) return
      if (totalProductsCurrentlyOnPage === 0 && page != 0) return
      if (!nextFeedURL) return

      setShowNextFeedLoading(true)

      const queryParams = getQueryParamsForSelectedFilters(selectedFilters, sortBy)
      collectionService
        .getCollectionById(
          {
            collectionId: collectionId,
            productIdForRecommendations: productId,
            colorForProductRecommendations: color,
            nextPageUrl: nextFeedURL,
            filter: queryParams.filter,
            page: page,
            sortBy: (queryParams.sortBy as PRODUCT_SORT_VALUES | undefined) ?? baseSortBy,
          },
          isI18nPreviewEnabled,
          false,
          isRecurringItemSubscriptionsExpEnabled,
        )
        .then(resp => {
          setPage(prev => prev + 1)

          const total = resp.total
          if (typeof total === 'number' && !isNaN(total)) {
            setTotalProductsCount(total)
          }

          const newFilteredSubCollections = filteredSubCollections
          if (newFilteredSubCollections[0]?.productCards && resp.subCollections[0]?.productCards) {
            newFilteredSubCollections[0].productCards = [
              ...newFilteredSubCollections[0].productCards,
              ...resp.subCollections[0].productCards,
            ]
          }

          setFilteredSubCollections(newFilteredSubCollections)
          setShowNextFeedLoading(false)
          setNextFeedURL(resp.next_url)
        })
        .catch((e: unknown) => {
          setShowNextFeedLoading(false)
          captureException(e, {
            module: MODULES.MINI_PDP,
            feature: FEATURES.PAGINATION,
          })
        })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isIntersecting, showSubCollectionsPageLoader])

  useEffect(() => {
    // Update the cache with the latest data after the component has done rendering
    queueMicrotask(() => {
      const updatedCollectionPage = {
        ...collectionPage,
        subCollections: filteredSubCollections,
        filters: initialFilters,
        total: totalProductsCount || 0,
        next_url: nextFeedURL || '',
        nextPageUrl: nextFeedURL || '',
      } as IProductFeedResponseForCollection

      collectionCacheStateManager.setCollectionCache(
        {
          collectionPage: updatedCollectionPage,
          page,
          hasPaginationTerminated: hasPaginationTerminated.current,
          scrollY: scrollY.current,
        },
        currentCacheKey.current,
      )
    })
  }, [
    filteredSubCollections,
    nextFeedURL,
    initialFilters,
    totalProductsCount,
    page,
    collectionPage,
    scrollY,
    currentCacheKey,
  ])

  const updateQuery = useCallback(
    ({ filter, sort }: UpdateQueryParams, options?: { skipFilterClickEvent?: boolean }) => {
      const { updatedSelectedFilters, updatedSortBy } = getUpdatedSelectedFilters({
        filter,
        sort,
        selectedFilters,
        sortBy,
        options,
        router,
        isNewCLPFiltersExperimentActive: true,
      })

      if (updatedSortBy === sortBy && SelectedFilter.checkEquality(updatedSelectedFilters, selectedFilters)) return

      const updatedWatchedQueryState = {
        filter: SelectedFilter.getUpdatedSelectedFilterStringForWindowURL(updatedSelectedFilters),
        sortBy: updatedSortBy === SORT_BY.FEATURED.value ? undefined : updatedSortBy,
      }

      handleFilterSectionScroll(filterSectionRef)
      resetCollectionCacheOnFilterChange({ pathname, updatedWatchedQueryState, searchParams })
      setWatchedQueryStates(updatedWatchedQueryState, null, false)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFilters, sortBy, router, setWatchedQueryStates],
  )

  const filteredSubCollection = filteredSubCollections[0]

  const tracker = `mini_product_page__${pathname}${getTrackerUrlForSelectedFilters(selectedFilters)}`

  return {
    collectionPage,
    totalProductsCount,
    initialFilters,
    sortBy,
    selectedFilters,
    updateQuery,
    filteredSubCollection,
    tracker,
    showSubCollectionsPageLoader,
    showNextFeedLoading,
    totalProductsCurrentlyOnPage,
    intersectRef,
    productViewTracker,
    filterSectionRef,
    productSectionRef,
    productCardsSectionRef,
    router,
  }
}
